import React, { useState, useEffect } from 'react';
import { ui, button } from 'util/ui';
import RowIconButton from './shell/RowIconButton';
import { DeleteIcon } from './shell/Icons';

/**
 * One note: title, body and Save. UI refresh styling (5 Nov 2026): inline title field, a rounded
 * writing area, an "Unsaved" pill and a primary Save button. Behaviour unchanged.
 */

type Props = {
  note: INote;
  deleteNote(id: string): void;
  onSave(note: INote): void;
  onChange(): void;
};

const NoteForm: React.FC<Props> = ({ note, deleteNote, onSave, onChange }) => {
  const [title, setTitle] = useState(note.title);
  const [body, setBody] = useState(note.body);
  const [requireSave, setRequireSave] = useState(false);

  useEffect(() => {
    setTitle(note.title);
    setBody(note.body);
  }, [note]);

  useEffect(() => {
    if (title && body) {
      if (note.title !== title || note.body !== body) {
        onChange();
        setRequireSave(true);
      } else {
        setRequireSave(false);
      }
    }
  }, [title, body]);

  const save = () => {
    onSave({ ...note, title: title, body: body });
    setRequireSave(false);
  };

  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 14px 14px', fontFamily: ui.font }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          className="archy-edit"
          aria-label="Note title"
          placeholder="Note title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={{
            flex: 1,
            minWidth: 0,
            height: 38,
            boxSizing: 'border-box',
            border: '1px solid transparent',
            borderRadius: 8,
            padding: '0 8px',
            marginLeft: -8,
            background: 'transparent',
            fontFamily: 'inherit',
            fontSize: 16,
            fontWeight: 700,
            color: ui.ink,
          }}
        />
        {requireSave && (
          <span style={{ fontSize: 12, fontWeight: 600, color: '#7A5300', background: '#FEF4D8', borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap' }}>
            Unsaved
          </span>
        )}
        <RowIconButton label="Delete note" danger onClick={() => deleteNote(note.id)}>
          <DeleteIcon color="#B3261E" size={16} />
        </RowIconButton>
      </div>
      <textarea
        aria-label="Note"
        placeholder="Write the note…"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        spellCheck={false}
        style={{
          flex: 1,
          minHeight: 160,
          resize: 'none',
          boxSizing: 'border-box',
          border: `1px solid ${ui.line}`,
          borderRadius: 12,
          padding: 12,
          fontFamily: 'inherit',
          fontSize: 14,
          lineHeight: 1.55,
          color: ui.ink,
          background: ui.surface,
          outline: 'none',
        }}
      />
      {requireSave && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="archy-btn" onClick={save} style={{ ...button('primary'), height: 38 }}>
            Save note
          </button>
        </div>
      )}
    </div>
  );
};

export default NoteForm;
